import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
	Button,
	Checkbox,
	EmptyState,
	Input,
	ReportIcon,
	SearchIcon,
	Spinner,
	Text,
	WarningIcon,
} from '@viapeople/components'
import { useMemo } from 'react'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import type { FormComponentLite, QuestionOption, ReportConfig, RoleGroup, RoleParticipant } from './types'

interface ConfigureReportsProps {
	selectedCycleId: string
	organizationId: string
	config: ReportConfig
	setConfig: (config: ReportConfig | ((prev: ReportConfig) => ReportConfig)) => void
	selectedParticipantIds: string[]
	setSelectedParticipantIds: (ids: string[] | ((prev: string[]) => string[])) => void
	selectedFormIds: string[]
	setSelectedFormIds: (ids: string[] | ((prev: string[]) => string[])) => void
	questionSearch: string
	setQuestionSearch: (search: string) => void
	formComponents: FormComponentLite[]
	formComponentsLoading: boolean
	formComponentsError: boolean
	roleGroups: RoleGroup[]
	isCycleAdminForSelectedCycle: boolean
	refetchReportData: () => void
}

export function ConfigureReports({
	selectedCycleId,
	organizationId,
	config,
	setConfig,
	selectedParticipantIds,
	setSelectedParticipantIds,
	selectedFormIds,
	setSelectedFormIds,
	questionSearch,
	setQuestionSearch,
	formComponents,
	formComponentsLoading,
	formComponentsError,
	roleGroups,
	isCycleAdminForSelectedCycle,
	refetchReportData,
}: ConfigureReportsProps) {
	const queryClient = useQueryClient()
	const { showToast } = useToast()

	const filteredFormWithQuestions = useMemo(() => {
		const formMap = new Map<string, { formId: string; formName: string; questions: QuestionOption[] }>()

		formComponents
			.filter((c) => c.type === 'input' || c.name === 'select' || c.name === 'rating')
			.forEach((c) => {
				const formId = c.formId
				if (!formId) return

				if (!formMap.has(formId)) {
					formMap.set(formId, {
						formId,
						formName: c.formName || `Form ${formId.slice(0, 8)}`,
						questions: [],
					})
				}

				formMap.get(formId)?.questions.push({
					id: c.id,
					label: c.label || c.name || 'Question',
				})
			})

		let forms = Array.from(formMap.values())

		if (selectedFormIds.length > 0) {
			forms = forms.filter((f) => selectedFormIds.includes(f.formId))
		}

		if (questionSearch.trim()) {
			const searchLower = questionSearch.toLowerCase()
			forms = forms
				.map((form) => ({
					...form,
					questions: form.questions.filter((q) => q.label.toLowerCase().includes(searchLower)),
				}))
				.filter((form) => form.questions.length > 0)
		}

		return forms
	}, [formComponents, selectedFormIds, questionSearch])

	const upsertConfigMutation = useMutation({
		...trpc.client.reports.upsertConfig.mutationOptions(),
		onSuccess: () => {
			showToast('Report configuration saved successfully')
		},
		onError: (error) => {
			showToast(`Failed to save configuration: ${error.message}`, 'error')
		},
	})

	const handleToggleQuestion = (questionId: string) => {
		setConfig((prev: ReportConfig) => {
			const exists = prev.includeQuestionIds.includes(questionId)
			const includeQuestionIds = exists
				? prev.includeQuestionIds.filter((id: string) => id !== questionId)
				: [...prev.includeQuestionIds, questionId]
			return { ...prev, includeQuestionIds }
		})
	}

	const handleToggleRole = (role: string) => {
		setConfig((prev: ReportConfig) => {
			const exists = prev.visibleRoles.includes(role)
			const visibleRoles = exists ? prev.visibleRoles.filter((r: string) => r !== role) : [...prev.visibleRoles, role]
			return { ...prev, visibleRoles }
		})
	}

	const handleSave = async () => {
		if (!selectedCycleId || !organizationId) return

		await upsertConfigMutation.mutateAsync({
			cycleId: selectedCycleId,
			includeQuestionIds: config.includeQuestionIds,
			visibleRoles: config.visibleRoles,
			organizationId,
			selectedParticipantIds,
			selectedFormIds,
		})

		await queryClient.invalidateQueries({
			queryKey: ['reports', 'getConfig', { cycleId: selectedCycleId }],
		})

		await new Promise((resolve) => setTimeout(resolve, 100))

		await queryClient.invalidateQueries({
			queryKey: ['reports', 'getMyReportData', { cycleId: selectedCycleId }],
		})

		await refetchReportData()
	}

	if (!selectedCycleId) {
		return (
			<div className='p-6'>
				<EmptyState
					icon={ReportIcon}
					title='Select a cycle to configure'
					description='Choose a cycle above to select which questions and whose responses appear in reports.'
				/>
			</div>
		)
	}

	if (!isCycleAdminForSelectedCycle) {
		return (
			<div className='p-6'>
				<EmptyState
					icon={ReportIcon}
					title='No access to configure'
					description='You are not a cycle admin for this cycle.'
				/>
			</div>
		)
	}

	return (
		<div className='p-6 space-y-8'>
			<div className='bg-white border border-neutral-200 rounded-lg'>
				<div className='px-6 py-4 border-b border-neutral-100'>
					<div className='flex items-center justify-between'>
						<div>
							<Text variant='h6' weight='semibold' className='mb-1'>
								Forms & Questions
							</Text>
							<Text variant='xsmall' className='text-neutral-500'>
								{config.includeQuestionIds.length} question
								{config.includeQuestionIds.length !== 1 ? 's' : ''} selected
							</Text>
						</div>
						{filteredFormWithQuestions.length > 0 && (
							<Button
								variant='secondary'
								size='small'
								onClick={() => {
									const allQuestionIds = filteredFormWithQuestions.flatMap((f) => f.questions.map((q) => q.id))
									const allSelected = allQuestionIds.every((id) => config.includeQuestionIds.includes(id))
									if (allSelected) {
										setConfig((prev) => ({ ...prev, includeQuestionIds: [] }))
									} else {
										setConfig((prev) => ({ ...prev, includeQuestionIds: allQuestionIds }))
									}
								}}>
								{filteredFormWithQuestions
									.flatMap((f) => f.questions)
									.every((q) => config.includeQuestionIds.includes(q.id))
									? 'Deselect All'
									: 'Select All'}
							</Button>
						)}
					</div>
				</div>

				<div className='p-6 space-y-6'>
					<div className='relative'>
						<SearchIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4' />
						<Input
							placeholder='Search all questions...'
							value={questionSearch}
							onChange={(e) => setQuestionSearch(e.target.value)}
							className='pl-10'
						/>
					</div>

					{formComponentsLoading ? (
						<div className='flex flex-col items-center justify-center py-12 gap-3'>
							<Spinner className='w-8 h-8 text-primary-500' />
							<Text variant='small' className='text-neutral-500'>
								Loading forms and questions...
							</Text>
						</div>
					) : formComponentsError ? (
						<EmptyState
							icon={WarningIcon}
							title='Failed to load forms'
							description='Something went wrong while loading forms. Please try selecting the cycle again or refresh the page.'
						/>
					) : filteredFormWithQuestions.length === 0 ? (
						<EmptyState
							icon={ReportIcon}
							title='No forms found'
							description="This cycle doesn't have any forms with questions yet."
						/>
					) : (
						<div className='space-y-4'>
							{filteredFormWithQuestions.map((form) => (
								<div key={form.formId} className='border border-neutral-200 rounded-lg'>
									<div className='p-4 bg-neutral-50 border-b border-neutral-200'>
										<div className='flex items-center justify-between'>
											<div className='flex items-center gap-3'>
												<Text variant='small' weight='semibold'>
													{form.formName}
												</Text>
												<Text variant='xsmall' className='text-neutral-500'>
													({form.questions.length} questions)
												</Text>
											</div>
											<Button
												variant='tertiary'
												size='small'
												onClick={() => {
													const formQuestionIds = form.questions.map((q) => q.id)
													const isFormSelected = formQuestionIds.every((id) => config.includeQuestionIds.includes(id))
													if (isFormSelected) {
														setConfig((prev) => ({
															...prev,
															includeQuestionIds: prev.includeQuestionIds.filter((id) => !formQuestionIds.includes(id)),
														}))
													} else {
														setConfig((prev) => ({
															...prev,
															includeQuestionIds: [...new Set([...prev.includeQuestionIds, ...formQuestionIds])],
														}))
													}
												}}>
												{form.questions.every((q) => config.includeQuestionIds.includes(q.id))
													? 'Deselect All'
													: 'Select All'}
											</Button>
										</div>
									</div>
									<div className='p-4 space-y-2 max-h-64 overflow-y-auto'>
										{form.questions.map((question: QuestionOption) => (
											<Checkbox
												key={question.id}
												label={question.label}
												checked={config.includeQuestionIds.includes(question.id)}
												onChange={() => handleToggleQuestion(question.id)}
											/>
										))}
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			<div className='bg-white border border-neutral-200 rounded-lg'>
				<div className='px-6 py-4 border-b border-neutral-100'>
					<div>
						<Text variant='h6' weight='semibold' className='mb-1'>
							Roles & Participants
						</Text>
						<Text variant='xsmall' className='text-neutral-500'>
							{config.visibleRoles.length} role{config.visibleRoles.length !== 1 ? 's' : ''} selected,{' '}
							{selectedParticipantIds.length} specific participant
							{selectedParticipantIds.length !== 1 ? 's' : ''}
						</Text>
					</div>
				</div>

				<div className='p-6 space-y-6'>
					{roleGroups && roleGroups.length > 0 ? (
						<div className='space-y-4'>
							{roleGroups.map((roleGroup: RoleGroup) => (
								<div key={roleGroup.role} className='border border-neutral-200 rounded-lg'>
									<div className='p-4 bg-neutral-50 border-b border-neutral-200'>
										<div className='flex items-center justify-between'>
											<div className='flex items-center gap-3'>
												<Checkbox
													label={`${roleGroup.roleName} (${roleGroup.count} participants)`}
													checked={config.visibleRoles.includes(roleGroup.role)}
													onChange={() => handleToggleRole(roleGroup.role)}
												/>
												{config.visibleRoles.includes(roleGroup.role) && (
													<div className='w-2 h-2 bg-primary-500 rounded-full'></div>
												)}
											</div>
										</div>
									</div>

									{config.visibleRoles.includes(roleGroup.role) && (
										<div className='p-4'>
											<div className='mb-3 flex items-center justify-between'>
												<Text variant='small' weight='semibold' className='text-neutral-700'>
													Specific Participants (Optional)
												</Text>
												<Button
													variant='tertiary'
													size='small'
													onClick={() => {
														const roleParticipantIds = roleGroup.participants.map((p: RoleParticipant) => p.id)
														const isRoleSelected = roleParticipantIds.every((id: string) =>
															selectedParticipantIds.includes(id),
														)
														if (isRoleSelected) {
															setSelectedParticipantIds((prev) => prev.filter((id) => !roleParticipantIds.includes(id)))
														} else {
															setSelectedParticipantIds((prev) => [...new Set([...prev, ...roleParticipantIds])])
														}
													}}>
													{roleGroup.participants.every((p: RoleParticipant) => selectedParticipantIds.includes(p.id))
														? 'Deselect All'
														: 'Select All'}
												</Button>
											</div>

											<div className='space-y-2 max-h-48 overflow-y-auto border border-neutral-200 rounded-lg p-3'>
												{roleGroup.participants.map((participant: RoleParticipant) => (
													<Checkbox
														key={participant.id}
														label={
															`${participant.firstName} ${participant.lastName}`.trim() ||
															participant.email ||
															participant.id
														}
														checked={selectedParticipantIds.includes(participant.id)}
														onChange={() => {
															setSelectedParticipantIds((prev) =>
																prev.includes(participant.id)
																	? prev.filter((id) => id !== participant.id)
																	: [...prev, participant.id],
															)
														}}
													/>
												))}
											</div>

											<Text variant='xsmall' className='text-neutral-600 mt-3'>
												{
													selectedParticipantIds.filter((id) =>
														roleGroup.participants.some((p: RoleParticipant) => p.id === id),
													).length
												}{' '}
												of {roleGroup.count} participants selected. Leave empty to include all.
											</Text>
										</div>
									)}
								</div>
							))}
						</div>
					) : (
						<EmptyState
							icon={ReportIcon}
							title='No participants found'
							description="This cycle doesn't have any participants with roles yet."
						/>
					)}
				</div>
			</div>

			<div className='bg-white border border-neutral-200 rounded-lg p-6'>
				{config.includeQuestionIds.length === 0 && (
					<div className='mb-4 p-4 bg-error-50 border border-error-200 rounded-lg flex items-start gap-3'>
						<div className='w-5 h-5 bg-error-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5'>
							<Text variant='xsmall' weight='semibold' className='text-error-600'>
								!
							</Text>
						</div>
						<div>
							<Text variant='xsmall' weight='semibold' className='text-error-800 mb-1'>
								Missing required selection
							</Text>
							<Text variant='xsmall' className='text-error-700'>
								Please select at least one question to include in the report.
							</Text>
						</div>
					</div>
				)}

				<div className='flex justify-between items-center'>
					<div className='flex items-center gap-4'>
						<Text variant='xsmall' className='text-neutral-600'>
							{config.includeQuestionIds.length} questions, {config.visibleRoles.length} roles selected
						</Text>
					</div>
					<div className='flex gap-3'>
						<Button
							variant='secondary'
							onClick={() => {
								setConfig({ includeQuestionIds: [], visibleRoles: [] })
								setSelectedParticipantIds([])
								setSelectedFormIds([])
							}}>
							Reset All
						</Button>
						<Button
							onClick={handleSave}
							disabled={upsertConfigMutation.isPending || config.includeQuestionIds.length === 0}>
							{upsertConfigMutation.isPending ? 'Saving...' : 'Save Configuration'}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
